<script lang="ts">
	import { Helper, Input, Select } from "flowbite-svelte";
	import { createEventDispatcher } from "svelte";
	import type { Field } from "#gen/forms/v1/field_pb.js";
	import { states } from "#lib/address.js";
	import DefaultValue from "#lib/components/page/components/form/DefaultValue.svelte";
	import FieldHeader from "#lib/components/page/components/form/FieldHeader.svelte";
	import type { FieldProps, TypeIcon } from "#lib/components/page/components/form/FieldProps.js";
	import { getDefaultAddressAsText } from "#lib/components/page/components/form/defaultValue.js";
	import { setStringValue } from "#lib/struct.js";
	import { ERR_REQUIRED } from "#lib/validation.js";
	import type { JsonObject } from "@bufbuild/protobuf";

	type AddressValue = Record<string, string | undefined> | null;
	type $$Props = Omit<FieldProps, "value"> & { value?: AddressValue };

	interface $$Events {
		input: CustomEvent<Field>;
		change: CustomEvent<Field>;
	}

	const dispatch = createEventDispatcher();

	export let typeIcon: TypeIcon | undefined = undefined;
	export let field: Field;
	export let editable = false;
	export let active = false;
	export let value: AddressValue = !editable ? ((field.defaultValue as AddressValue) ?? null) : null;
	export let readOnly = false;

	const inputDisabled = (editable && field.immutable) || readOnly || !field.active;
	let errors: Record<string, string> = {};

	$: !editable && (value = (field.defaultValue as AddressValue) ?? null);
	$: defaultAddressValue = field.defaultValue as AddressValue;

	export async function validate(): Promise<boolean> {
		let valid = true;
		errors = {};
		for (const key of ["address1", "city", "zone", "postalCode"]) {
			valid = validateRequired(key, value?.[key]) && valid;
		}
		return valid;
	}

	function validateRequired(key: string, value: unknown): boolean {
		if (field.required && !value) {
			errors[key] = ERR_REQUIRED;
			return false;
		}
		return true;
	}

	function handleValueChange(event: Event, key: string) {
		const target = event.target as HTMLInputElement;
		if (!editable) {
			setValueField(key, target.value);
		} else {
			setDefaultValueField(key, target.value);
			dispatch("input", field);
		}
	}

	function setValueField(key: string, fieldValue: string) {
		if (!value) {
			value = {};
		}
		value[key] = fieldValue;
	}

	function setDefaultValueField(key: string, fieldValue: string) {
		if (!field.defaultValue) {
			field.defaultValue = {} as JsonObject;
		}
		setStringValue(field.defaultValue, key, fieldValue);
		field = field;
	}

	function handleFieldChange() {
		dispatch("change", field);
	}
</script>

<div {...$$restProps}>
	{#if editable}
		<FieldHeader {typeIcon} {active} {field} on:input on:change />
	{:else}
		<label for={field.name} class="mb-2 inline-block text-sm font-medium" class:required={field.required}>
			{field.title}
		</label>
		{#if field.description}
			<div class="field-description my-2 text-xs text-gray-500">{field.description}</div>
		{/if}
	{/if}
	{#if field.static && !editable}
		<DefaultValue>{getDefaultAddressAsText(field.defaultValue)}</DefaultValue>
	{:else}
		<div class="mb-4">
			<Input
				class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""}
				color={errors["address1"] ? "red" : "base"}
				type="text"
				value={value?.["address1"] ?? defaultAddressValue?.["address1"]}
				name="{field.name}.address1"
				id="{field.name}.address1"
				disabled={inputDisabled}
				autocomplete="address-line1"
				on:input={e => handleValueChange(e, "address1")}
				on:change={handleFieldChange}
			/>
			{#if errors["address1"]}
				<Helper class="mt-2" color="red"><span class="font-medium">{errors["address1"]}</span></Helper>
			{/if}
		</div>
		<div class="mb-4">
			<label for="{field.name}.address1" class="mb-2 inline-block text-sm font-medium">Apt, suite, etc</label>
			<Input
				class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""}
				color={errors["address2"] ? "red" : "base"}
				type="text"
				value={value?.["address2"] ?? defaultAddressValue?.["address2"]}
				name="{field.name}.address2"
				id="{field.name}.address2"
				disabled={inputDisabled}
				autocomplete="address-line2"
				on:input={e => handleValueChange(e, "address2")}
				on:change={handleFieldChange}
			/>
			{#if errors["address2"]}
				<Helper class="mt-2" color="red"><span class="font-medium">{errors["address2"]}</span></Helper>
			{/if}
		</div>
		<div class="mb-4 grid grid-cols-2 gap-4">
			<div>
				<label for="{field.name}.city" class="mb-2 inline-block text-sm font-medium" class:required={field.required}>City</label>
				<Input
					class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""}
					color={errors["city"] ? "red" : "base"}
					type="text"
					value={value?.["city"] ?? defaultAddressValue?.["city"]}
					name="{field.name}.city"
					id="{field.name}.city"
					disabled={inputDisabled}
					autocomplete="address-level2"
					on:input={e => handleValueChange(e, "city")}
					on:change={handleFieldChange}
				/>
				{#if errors["city"]}
					<Helper class="mt-2" color="red"><span class="font-medium">{errors["city"]}</span></Helper>
				{/if}
			</div>
			<div>
				<label for="{field.name}.zone" class="mb-2 inline-block text-sm font-medium" class:required={field.required}>State</label>
				<!-- Tight wrapper so the styled-form chevron (div:has(> select)::after in Form.svelte) centers on the select rather than this cell, which also holds the label. -->
				<div>
					<Select
						class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""}
						color={errors["zone"] ? "red" : "base"}
						value={value?.["zone"] ?? defaultAddressValue?.["zone"]}
						name="{field.name}.zone"
						id="{field.name}.zone"
						items={states}
						disabled={inputDisabled}
						autocomplete="address-level1"
						on:input={e => handleValueChange(e, "zone")}
						on:change={handleFieldChange}
					/>
				</div>
				{#if errors["zone"]}
					<Helper class="mt-2" color="red"><span class="font-medium">{errors["zone"]}</span></Helper>
				{/if}
			</div>
			<div>
				<label for="{field.name}.postalCode" class="mb-2 inline-block text-sm font-medium" class:required={field.required}>Zip</label>
				<Input
					class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""}
					color={errors["postalCode"] ? "red" : "base"}
					value={value?.["postalCode"] ?? defaultAddressValue?.["postalCode"]}
					type="text"
					name="{field.name}.postalCode"
					id="{field.name}.postalCode"
					disabled={inputDisabled}
					autocomplete="postal-code"
					on:input={e => handleValueChange(e, "postalCode")}
					on:change={handleFieldChange}
				/>
				{#if errors["postalCode"]}
					<Helper class="mt-2" color="red"><span class="font-medium">{errors["postalCode"]}</span></Helper>
				{/if}
			</div>
			<div>
				<label for="{field.name}.country" class="mb-2 inline-block text-sm font-medium" class:required={field.required}>Country</label>
				<Input
					class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""}
					color={errors["country"] ? "red" : "base"}
					value={value?.["country"] ?? defaultAddressValue?.["country"]}
					type="text"
					name="{field.name}.country"
					id="{field.name}.country"
					disabled={inputDisabled}
					autocomplete="country"
					on:input={e => handleValueChange(e, "country")}
					on:change={handleFieldChange}
				/>
				{#if errors["country"]}
					<Helper class="mt-2" color="red"><span class="font-medium">{errors["country"]}</span></Helper>
				{/if}
			</div>
		</div>
	{/if}
</div>
